import React from 'react';
import type { AssistantPhase } from '../../types/conversation';
import DevSpottSymbolLoader from './DevSpottSymbolLoader';

interface AiThinkingIndicatorProps {
  phase: AssistantPhase | undefined;
  status?: string;
  /**
   * 'bubble' — compact inline indicator (standard chat mode)
   * 'canvas' — centred, fills the canvas placeholder area
   */
  variant?: 'bubble' | 'canvas';
}

/**
 * Indicador de “thinking” da IA: símbolo DevSpott animado + texto com shimmer ou skeleton.
 *
 * Phases:
 *  - thinking (no status yet) → skeleton rows shimmer
 *  - thinking (with status)   → símbolo + shimmer no status
 *  - answering                → caret visível no caller; aqui retorna null
 *  - done / error             → null
 */
const AiThinkingIndicator: React.FC<AiThinkingIndicatorProps> = ({
  phase,
  status,
  variant = 'bubble',
}) => {
  if (!phase || phase === 'done' || phase === 'error' || phase === 'answering') {
    return null;
  }

  const hasStatus = Boolean(status && status.trim());

  if (variant === 'canvas') {
    return (
      <div
        className="flex flex-col items-center justify-center h-full min-h-[400px] py-8 relative z-10"
        aria-live="polite"
        aria-label="Processando resposta"
      >
        <DevSpottSymbolLoader size="lg" className="mb-8" />

        {/* Status text or skeleton rows */}
        {hasStatus ? (
          <p className="ai-shimmer-text text-xs font-medium text-center max-w-[min(100%,260px)] px-4 break-words leading-snug">
            {status}
          </p>
        ) : (
          <div className="w-48 flex flex-col gap-2.5">
            <div className="ai-skeleton-row h-2.5 w-full" />
            <div className="ai-skeleton-row h-2.5 w-3/4" />
            <div className="ai-skeleton-row h-2.5 w-1/2" />
          </div>
        )}
      </div>
    );
  }

  /* ── bubble variant ───────────────────────────────────────────── */
  return (
    <div
      className="flex flex-col gap-2 py-2.5 min-w-0"
      aria-live="polite"
      aria-label="Processando resposta"
    >
      <div className="flex items-center gap-3 px-0.5 min-w-0">
        <DevSpottSymbolLoader size="sm" />

        {hasStatus ? (
          <p className="ai-shimmer-text text-xs font-medium flex-1 min-w-0 w-full break-words leading-snug text-left">
            {status}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 flex-1 min-w-0">
            <div className="ai-skeleton-row h-2 w-32" />
            <div className="ai-skeleton-row h-2 w-20" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AiThinkingIndicator;
